import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";

import {
  Package,
  Filter,
  X,
  Eye,
  Edit,
  Play,
  XCircle,
  PlusCircle,
  MoreVertical,
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Plus,
  Send,
  Search as SearchIcon,
  Trash2,
  Info,
  ScanBarcode,
  Wand2,
  Download,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn, formatDate } from "@/lib/utils";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { ReactSelect } from "@/components/ui/react-select";
import Modal from "@/components/ui/modal";
import RecordNotFound from "@/core/components/RecordNotFound";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PendingApprovals from "./pendingApprovals";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const StockAdjustments = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [activeSerialIndex, setActiveSerialIndex] = useState(null);
  const [serialGenParams, setSerialGenParams] = useState({
    startNumber: "",
    quantity: 1,
    prefix: "",
  });
  const [newAdjustmentData, setNewAdjustmentData] = useState({
    store_id: "",
    reason: "physical_count",
    notes: "",
    items: [],
    status: "draft",
    requires_approval: true,
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => { },
    variant: "default",
  });

  const [inputDialog, setInputDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    label: "",
    placeholder: "",
    value: "",
    onConfirm: () => { },
    isRequired: false,
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const currentStore = useSelector((state) => state.current_store);
  const currentUser = useSelector((state) => state.user_data);
  const currency = currentStore?.default_currency;
  const currencySymbol = currency?.symbol || "";

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSettings, setExportSettings] = useState({
    store_id: "",
    status: [],
    reason: [],
    created_from: "",
    created_to: "",
    min_adjustment_value: "",
    max_adjustment_value: "",
    min_items: "",
    max_items: "",
  });

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [filters, setFilters] = useState({
    store_id: "",
    status: [],
    reason: [],
    created_from: "",
    created_to: "",
    min_adjustment_value: "",
    max_adjustment_value: "",
    min_items: "",
    max_items: "",
    sort_by: "created_at",
    sort_direction: "desc",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    store_id: "",
    status: [],
    reason: [],
    created_from: "",
    created_to: "",
    min_adjustment_value: "",
    max_adjustment_value: "",
    min_items: "",
    max_items: "",
    sort_by: "created_at",
    sort_direction: "desc",
  });

  useEffect(() => {
    if (currentStore?.id) {
      setNewAdjustmentData((prev) => ({
        ...prev,
        store_id: currentStore.id,
      }));
      setFilters((prev) => ({
        ...prev,
        store_id: currentStore.id.toString(),
      }));
      setAppliedFilters((prev) => ({
        ...prev,
        store_id: currentStore.id.toString(),
      }));
    }
  }, [currentStore]);

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
    enabled: hasPermission("store read"),
  });

  const { data: posProducts = [], isFetching: isSearchingProducts } = useQuery({
    queryKey: ["all-pos-products", productSearch],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          per_page: 20,
          search: productSearch || undefined,
          with: ["brand", "model", "unit", "categories"],
        },
      });

      return res.data.data || [];
    },
    enabled: hasPermission("product read"),
    keepPreviousData: true,
  });

  const { data: adjustmentsData, isLoading } = useQuery({
    queryKey: [
      "stockAdjustments",
      currentPage,
      pageSize,
      appliedFilters,
      searchText,
      currentUser?.id,
      hasPermission("stock adjustment view_all"),
    ],
    queryFn: async () => {
      const params = {
        page: currentPage,
        per_page: pageSize,
        with: "store",
        search: searchText,
        ...appliedFilters,
      };

      if (
        !hasPermission("stock adjustment view_all") &&
        hasPermission("stock adjustment view_own")
      ) {
        params.user_id = currentUser?.id;
      }

      // Ensure reason and status are arrays if they have values
      if (params.reason && !Array.isArray(params.reason)) {
        params.reason = [params.reason];
      }
      if (params.status && !Array.isArray(params.status)) {
        params.status = [params.status];
      }

      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          (Array.isArray(params[key]) && params[key].length === 0)
        ) {
          delete params[key];
        }
      });

      const res = await apiClient.get(API_ENDPOINTS.STOCK.ADJUSTMENTS, {
        params,
        paramsSerializer: {
          serialize: (p) => {
            const searchParams = new URLSearchParams();
            Object.keys(p).forEach((key) => {
              const value = p[key];
              if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(`${key}[]`, v));
              } else if (value !== undefined && value !== null) {
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          },
        },
      });
      return res.data;
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to fetch stock adjustments",
      );
    },
  });

  const adjustments = adjustmentsData?.data || [];
  const paginationMeta = {
    current_page:
      adjustmentsData?.meta?.current_page || adjustmentsData?.current_page || 1,
    last_page:
      adjustmentsData?.meta?.last_page || adjustmentsData?.last_page || 1,
    per_page:
      adjustmentsData?.meta?.per_page || adjustmentsData?.per_page || 10,
    total:
      adjustmentsData?.meta?.total ||
      adjustmentsData?.total ||
      adjustments.length,
  };

  const cancelMutation = useMutation({
    mutationFn: async ({ id, reason }) => {
      const payload = {};
      if (reason && reason.trim()) {
        payload.reason = reason.trim();
      }
      await apiClient.post(API_ENDPOINTS.STOCK.CANCEL(id), payload);
    },
    onSuccess: () => {
      toast.success("Stock adjustment cancelled successfully");
      queryClient.invalidateQueries(["stockAdjustments"]);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to cancel stock adjustment",
      );
    },
  });

  const processMutation = useMutation({
    mutationFn: async ({ id, notes }) => {
      await apiClient.post(
        API_ENDPOINTS.STOCK.PROCESS(id),
        { notes, force_process: false },
      );
    },
    onSuccess: () => {
      toast.success("Stock adjustment processed successfully");
      queryClient.invalidateQueries(["stockAdjustments"]);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to process stock adjustment",
      );
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.post(API_ENDPOINTS.STOCK.SUBMIT(id), {});
    },
    onSuccess: () => {
      toast.success("Stock adjustment submitted for approval");
      queryClient.invalidateQueries(["stockAdjustments"]);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to submit stock adjustment",
      );
    },
  });

  const validateMutation = useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.post(
        API_ENDPOINTS.STOCK.VALIDATE(id),
        {},
      );
      return res.data;
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Validation failed for this adjustment",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(API_ENDPOINTS.STOCK.ADJUSTMENT_DETAILS(id));
    },
    onSuccess: () => {
      toast.success("Stock adjustment deleted successfully");
      queryClient.invalidateQueries(["stockAdjustments"]);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to delete stock adjustment",
      );
    },
  });

  const handleCancel = (id) => {
    setInputDialog({
      isOpen: true,
      title: "Cancel Adjustment?",
      description: "You won't be able to revert this!",
      label: "Cancellation Reason (Optional)",
      placeholder: "Enter reason for cancellation...",
      value: "",
      onConfirm: (reason) => {
        cancelMutation.mutate({ id, reason });
      },
      isRequired: false,
    });
  };

  const handleProcess = async (id) => {
    try {
      // Step 1: Validate items
      const validation = await validateMutation.mutateAsync(id);
      const validationData = validation?.data || {};
      const { is_valid, errors, warnings } = validationData;

      // Handle Errors
      if (
        !is_valid &&
        errors &&
        (Array.isArray(errors)
          ? errors.length > 0
          : Object.keys(errors).length > 0)
      ) {
        const errorList = Array.isArray(errors)
          ? errors
          : Object.values(errors).flat();

        setConfirmDialog({
          isOpen: true,
          title: "Validation Errors",
          description: (
            <div className="space-y-2 text-left">
              <p className="font-medium text-destructive">
                This adjustment cannot be processed due to the following errors:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {errorList.map((err, i) => (
                  <li key={i} className="text-sm">
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          ),
          onConfirm: () => { },
          variant: "default",
        });
        return;
      }

      // Handle Warnings
      const warningList = warnings ? Object.values(warnings).flat() : [];

      if (warningList.length > 0) {
        setConfirmDialog({
          isOpen: true,
          title: "Process with Warnings?",
          description: (
            <div className="space-y-2 text-left">
              <p className="font-medium text-amber-600">
                The following warnings were detected:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {warningList.map((warn, i) => (
                  <li key={i} className="text-sm">
                    {warn}
                  </li>
                ))}
              </ul>
              <p className="pt-2">
                Are you sure you want to process this adjustment anyway?
              </p>
            </div>
          ),
          onConfirm: () => {
            // Re-trigger the notes dialog
            setTimeout(() => {
              setInputDialog({
                isOpen: true,
                title: "Process Adjustment?",
                description:
                  "This will process the stock adjustment and update inventory.",
                label: "Processing Notes (Optional)",
                placeholder: "Enter any notes for processing...",
                value: "",
                onConfirm: (notes) => {
                  processMutation.mutate({ id, notes });
                },
                isRequired: false,
              });
            }, 100);
          },
        });
        return;
      }

      // Step 2: Show processing dialog if valid and no warnings
      setInputDialog({
        isOpen: true,
        title: "Process Adjustment?",
        description:
          "Validation successful. This will process the stock adjustment and update inventory.",
        label: "Processing Notes (Optional)",
        placeholder: "Enter any notes for processing...",
        value: "",
        onConfirm: (notes) => {
          processMutation.mutate({ id, notes });
        },
        isRequired: false,
      });
    } catch {
      // Error handled by validateMutation.onError
    }
  };

  const handleSubmitAdjustment = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Submit for Approval?",
      description: "Once submitted, you won't be able to edit this adjustment.",
      onConfirm: () => {
        submitMutation.mutate(id);
      },
      variant: "default",
    });
  };

  const handleDeleteAdjustment = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Adjustment?",
      description: "This action cannot be undone!",
      onConfirm: () => {
        deleteMutation.mutate(id);
      },
      variant: "destructive",
    });
  };

  const handleAddProduct = async (product) => {
    if (!product) return;

    if (newAdjustmentData.items.find((i) => i.product_id === product.id)) {
      toast.error("Product already added");
      return;
    }

    let inventoryId =
      product.inventory_id ||
      product.batches?.[0]?.inventory_id ||
      product.serial_numbers?.[0]?.inventory_id ||
      null;

    if (!inventoryId && product.id) {
      try {
        const invRes = await apiClient.get(
          API_ENDPOINTS.INVENTORY.BY_PRODUCT(product.id)
        );
        const inventories = Array.isArray(invRes.data?.data)
          ? invRes.data.data
          : invRes.data?.data
            ? [invRes.data.data]
            : [];
        const match = newAdjustmentData.store_id
          ? inventories.find(
            (inv) => String(inv.store_id) === String(newAdjustmentData.store_id)
          )
          : inventories[0];
        if (match?.id) {
          inventoryId = match.id;
        }
      } catch {
        /* silent */
      }
    }

    const newItem = {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      inventory_id: inventoryId,
      inventory_batch_id: null,
      adjustment_quantity: 0,
      current_quantity: product.sellable_qty || 0,
      unit_cost: parseFloat(product.cost || 0),
      reason_notes: "",
      tracking_type: product.tracking_type || "none",
      available_batches: [],
      available_serials: [],
      serial_numbers: [],
    };

    if (newItem.tracking_type === "standard") {
      if (product.batches && product.batches.length > 0) {
        newItem.available_batches = product.batches;
      } else {
        try {
          const res = await apiClient.get(
            API_ENDPOINTS.INVENTORY.BATCHES_AVAILABLE(
              product.id,
              newAdjustmentData.store_id,
            ),
          );
          newItem.available_batches = res.data.data || [];
        } catch {
          toast.error("Failed to fetch available batches");
        }
      }

      if (newItem.available_batches?.length === 1 && !newItem.inventory_batch_id) {
        newItem.inventory_batch_id = newItem.available_batches[0].id.toString();
        newItem.current_quantity = newItem.available_batches[0].quantity;
        if (newItem.available_batches[0].unit_cost) {
          newItem.unit_cost = newItem.available_batches[0].unit_cost;
        }
      }
    }

    if (newItem.tracking_type === "serial") {
      if (product.serial_numbers && product.serial_numbers.length > 0) {
        newItem.available_serials = product.serial_numbers;
      } else {
        try {
          const res = await apiClient.get(
            API_ENDPOINTS.INVENTORY.SERIALS_AVAILABLE,
            {
              params: {
                product_id: product.id,
                store_id: newAdjustmentData.store_id,
              },
            },
          );
          newItem.available_serials = res.data.data || [];
        } catch {
          toast.error("Failed to fetch available serials");
        }
      }
    }

    setNewAdjustmentData((prev) => {
      const willHaveInventory =
        newItem.inventory_id || prev.items.some((it) => it.inventory_id);
      let updatedReason = prev.reason;
      if (willHaveInventory && prev.reason === "opening_balance") {
        updatedReason = "physical_count";
        toast.info(
          "Adjustment reason switched to Physical Count because products with existing inventory records cannot use Opening Balance."
        );
      }
      return {
        ...prev,
        reason: updatedReason,
        items: [...prev.items, newItem],
      };
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = [...newAdjustmentData.items];
    newItems.splice(index, 1);
    setNewAdjustmentData({ ...newAdjustmentData, items: newItems });
  };

  const handleItemChange = async (index, field, value) => {
    const newItems = [...newAdjustmentData.items];
    const item = { ...newItems[index] };

    if (field === "inventory_batch_id") {
      item.inventory_batch_id = value;
      const selectedBatch = item.available_batches.find((b) => b.id === value);
      if (selectedBatch) {
        item.current_quantity = selectedBatch.quantity;
        item.unit_cost = selectedBatch.unit_cost;
      }
    } else if (field === "serial_numbers") {
      item.serial_numbers = value;
      // Sync adjustment_quantity with serial count
      if (item.tracking_type === "serial") {
        const isNegative = item.adjustment_quantity < 0;
        const validCount = value.filter((s) => s.trim() !== "").length;
        item.adjustment_quantity = isNegative ? -validCount : validCount;
      }
    } else if (field === "adjustment_quantity") {
      item.adjustment_quantity = value;
      // For serial tracked, if we switch sign, we might need to clear selected serials
      if (item.tracking_type === "serial") {
        // Clear serials if swapping between positive and negative
        // Actually it's easier to just clear them if the count doesn't match and it's a manual edit
      }
    } else {
      item[field] = value;
    }

    newItems[index] = item;
    setNewAdjustmentData({ ...newAdjustmentData, items: newItems });
  };

  const saveNewMutation = useMutation({
    mutationFn: async (payload) => {
      // Remove internal flags before sending to API
      const apiPayload = { ...payload };
      delete apiPayload.shouldSubmit;

      if (isEditing && editingId) {
        const res = await apiClient.put(
          API_ENDPOINTS.STOCK.ADJUSTMENT_DETAILS(editingId),
          apiPayload,
        );
        return res.data.data;
      } else {
        const res = await apiClient.post(
          API_ENDPOINTS.STOCK.ADJUSTMENTS,
          apiPayload,
        );
        return res.data.data;
      }
    },
    onSuccess: async (data, variables) => {
      const wasPending = variables.shouldSubmit;

      toast.success(
        `Stock adjustment ${isEditing ? "updated" : "created"} successfully`,
      );

      if (wasPending && data?.id) {
        try {
          await submitMutation.mutateAsync(data.id);
        } catch {
          // Toast handled by submitMutation.onError
        }
      }

      setShowAddModal(false);
      setIsEditing(false);
      setEditingId(null);
      setNewAdjustmentData({
        store_id: currentStore?.id || "",
        reason: "physical_count",
        notes: "",
        items: [],
        status: "draft",
        requires_approval: true,
      });
      queryClient.invalidateQueries(["stockAdjustments"]);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message ||
        `Failed to ${isEditing ? "update" : "create"} stock adjustment`,
      );
    },
  });

  const handleSaveNewAdjustment = async (shouldSubmit = false) => {
    if (!newAdjustmentData.store_id || newAdjustmentData.items.length === 0) {
      toast.error("Please fill in all required fields");
      return;
    }

    const itemsWithoutInv = newAdjustmentData.items.filter(
      (item) => !item.inventory_id
    );
    const itemsWithInv = newAdjustmentData.items.filter(
      (item) => item.inventory_id
    );

    // If there are items without inventory, process them as opening stock
    if (itemsWithoutInv.length > 0) {
      for (const item of itemsWithoutInv) {
        const qty = parseInt(item.adjustment_quantity) || 0;
        if (qty <= 0) {
          toast.error(
            `Opening stock quantity for "${item.product_name}" must be greater than 0`
          );
          return;
        }
        if (item.tracking_type === "serial") {
          const serials = (item.serial_numbers || []).filter(
            (s) => s.trim() !== ""
          );
          if (serials.length !== qty) {
            toast.error(
              `Number of serial numbers (${serials.length}) must match opening quantity (${qty}) for "${item.product_name}"`
            );
            return;
          }
        }
      }

      try {
        const openingPayload = {
          store_id: newAdjustmentData.store_id,
          notes: newAdjustmentData.notes || "Initial opening stock",
          items: itemsWithoutInv.map((item) => ({
            product_id: item.product_id,
            opening_quantity: parseInt(item.adjustment_quantity) || 0,
            unit_cost: parseFloat(item.unit_cost) || null,
            reorder_level: null,
            location: null,
            sku: item.sku || null,
            reason_notes:
              item.reason_notes ||
              newAdjustmentData.notes ||
              "Initial opening stock",
            serial_numbers: (item.serial_numbers || []).filter(
              (s) => s.trim() !== ""
            ),
            meta: null,
          })),
        };
        await apiClient.post(
          API_ENDPOINTS.STOCK.OPENING_BALANCE,
          openingPayload
        );
        toast.success(
          "Opening stock created for products without inventory records"
        );
      } catch (err) {
        toast.error(
          err.response?.data?.message || "Failed to create opening stock"
        );
        return;
      }
    }

    // If there are items with existing inventory records, process standard stock adjustment
    if (itemsWithInv.length > 0) {
      if (newAdjustmentData.reason === "opening_balance") {
        toast.error(
          "Opening stock can only be added if there are no existing inventory records. Please remove items with existing inventory or choose a different adjustment reason."
        );
        return;
      }

      const missingBatch = itemsWithInv.find(
        (item) =>
          (item.tracking_type === "standard" ||
            (item.available_batches && item.available_batches.length > 0)) &&
          !item.inventory_batch_id
      );
      if (missingBatch) {
        toast.error(
          `Please select a batch for "${missingBatch.product_name}". Batch is required for stock adjustments.`
        );
        return;
      }

      const payload = {
        ...newAdjustmentData,
        status: "draft",
        shouldSubmit,
        items: itemsWithInv.map((item) => ({
          product_id: item.product_id,
          inventory_id: item.inventory_id,
          inventory_batch_id: item.inventory_batch_id || null,
          current_quantity: parseInt(item.current_quantity) || 0,
          adjustment_quantity: parseInt(item.adjustment_quantity) || 0,
          unit_cost: parseFloat(item.unit_cost) || 0,
          reason_notes: item.reason_notes || null,
          serial_numbers: (item.serial_numbers || []).filter(
            (s) => s.trim() !== ""
          ),
        })),
      };

      saveNewMutation.mutate(payload);
    } else {
      setShowAddModal(false);
      setNewAdjustmentData({
        store_id: currentStore?.id || "",
        reason: "physical_count",
        notes: "",
        items: [],
        status: "draft",
        requires_approval: true,
      });
      queryClient.invalidateQueries(["stockAdjustments"]);
      queryClient.invalidateQueries(["all-pos-products"]);
    }
  };

  const handleView = async (id) => {
    try {
      setSelectedAdjustment(null);
      setIsViewLoading(true);
      setShowViewModal(true);

      const res = await apiClient.get(API_ENDPOINTS.STOCK.ADJUSTMENT_DETAILS(id), {
        params: {
          with: "store,items.product,items.inventory,creator,approver,processor",
        },
      });

      if (res.status === 200) {
        setSelectedAdjustment(res.data.data);
      }
    } catch (err) {
      setShowViewModal(false);
      toast.error(
        err.response?.data?.message || "Failed to load adjustment details",
      );
    } finally {
      setIsViewLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.STOCK.ADJUSTMENT_DETAILS(id), {
        params: {
          with: "store,items.product,items.inventory",
        },
      });

      if (res.status === 200) {
        const adjustment = res.data.data;
        setNewAdjustmentData({
          store_id: adjustment.store_id || "",
          reason: adjustment.reason || "physical_count",
          notes: adjustment.notes || "",
          items: adjustment.items.map((item) => {
            const isSerialTracked = item.product?.tracking_type === "serial";
            const serialsLength = item.serial_numbers?.length || 0;

            let qty = item.adjustment_quantity;
            if (isSerialTracked && serialsLength > 0) {
              qty = serialsLength;
            }

            if (item.is_negative_adjustment) {
              qty = -Math.abs(qty || 0);
            } else if (item.is_positive_adjustment) {
              qty = Math.abs(qty || 0);
            }

            return {
              product_id: item.product_id,
              product_name: item.product?.name || "Product",
              sku: item.product?.sku || "",
              inventory_id: item.inventory_id,
              inventory_batch_id: item.inventory_batch_id,
              adjustment_quantity: qty,
              current_quantity: item.current_quantity,
              unit_cost: parseFloat(item.unit_cost || 0),
              reason_notes: item.reason_notes || "",
              tracking_type: item.product?.tracking_type || "none",
              available_batches: [],
              available_serials: [],
              serial_numbers: item.serial_numbers || [],
            };
          }),
          status: adjustment.status,
          requires_approval: adjustment.requires_approval ?? true,
        });
        setEditingId(id);
        setIsEditing(true);
        setShowAddModal(true);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to load adjustment for editing",
      );
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      store_id: currentStore?.id?.toString() || "",
      status: [],
      reason: [],
      created_from: "",
      created_to: "",
      min_adjustment_value: "",
      max_adjustment_value: "",
      min_items: "",
      max_items: "",
      sort_by: "created_at",
      sort_direction: "desc",
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setCurrentPage(1);
  };

  const filteredData = adjustments;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const handleOpenExport = () => {
    setExportSettings({
      store_id: appliedFilters.store_id || currentStore?.id?.toString() || "",
      status: appliedFilters.status || [],
      reason: appliedFilters.reason || [],
      created_from: appliedFilters.created_from || "",
      created_to: appliedFilters.created_to || "",
      min_adjustment_value: appliedFilters.min_adjustment_value || "",
      max_adjustment_value: appliedFilters.max_adjustment_value || "",
      min_items: appliedFilters.min_items || "",
      max_items: appliedFilters.max_items || "",
    });
    setShowExportModal(true);
  };

  const handleExport = async () => {
    try {
      const params = {
        ...exportSettings,
        search: searchText,
      };

      if (params.reason && !Array.isArray(params.reason)) {
        params.reason = [params.reason];
      }
      if (params.status && !Array.isArray(params.status)) {
        params.status = [params.status];
      }

      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          (Array.isArray(params[key]) && params[key].length === 0)
        ) {
          delete params[key];
        }
      });

      const res = await apiClient.get(API_ENDPOINTS.STOCK.EXPORT, {
        params,
        paramsSerializer: {
          serialize: (p) => {
            const searchParams = new URLSearchParams();
            Object.keys(p).forEach((key) => {
              const value = p[key];
              if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(`${key}[]`, v));
              } else if (value !== undefined && value !== null) {
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          },
        },
      });

      let csvString = "";

      if (typeof res.data === "string") {
        csvString = res.data;
      } else {
        const dataArray = res.data?.data || res.data || [];
        if (!Array.isArray(dataArray) || dataArray.length === 0) {
          toast.error("No data available to export");
          return;
        }

        const headersKey = Object.keys(dataArray[0]);
        const csvRows = [];

        csvRows.push(
          headersKey.map((k) => `"${String(k).replace(/"/g, '""')}"`).join(","),
        );

        for (const row of dataArray) {
          const values = headersKey.map((k) => {
            let val =
              row[k] === null || row[k] === undefined ? "" : String(row[k]);
            val = val.replace(/"/g, '""');
            return `"${val}"`;
          });
          csvRows.push(values.join(","));
        }
        csvString = csvRows.join("\n");
      }

      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);

      const pad = (n) => String(n).padStart(2, "0");
      const d = new Date();
      const timestamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      let fileName = `stock_adjustments_${timestamp}.csv`;

      const disposition = res.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          const originalName = matches[1].replace(/['"]/g, "");
          // Insert timestamp before the extension
          const nameParts = originalName.split(".");
          const ext = nameParts.length > 1 ? `.${nameParts.pop()}` : "";
          fileName = `${nameParts.join(".")}_${timestamp}${ext}`;
        }
      }

      const linkEl = document.createElement("a");
      linkEl.href = url;
      linkEl.setAttribute("download", fileName);
      document.body.appendChild(linkEl);
      linkEl.click();
      document.body.removeChild(linkEl);
      window.URL.revokeObjectURL(url);

      toast.success("Export successful");
      setShowExportModal(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to export stock adjustments",
      );
    }
  };

  const getStatusBadgeVariant = (status) => {
    const statusMap = {
      processed: "success",
      approved: "info",
      pending: "warning",
      draft: "secondary",
      rejected: "danger",
      cancelled: "danger",
    };
    return statusMap[status] || "secondary";
  };

  const reasonOptions = [
    { value: "damaged", label: "Damaged" },
    { value: "expired", label: "Expired" },
    { value: "stolen", label: "Stolen" },
    { value: "lost", label: "Lost" },
    { value: "found", label: "Found" },
    { value: "opening_balance", label: "Opening Balance" },
    { value: "physical_count", label: "Physical Count" },
    { value: "system_error", label: "System Error" },
    { value: "customer_return", label: "Customer Return" },
    { value: "supplier_return", label: "Supplier Return" },
    { value: "write_off", label: "Write Off" },
    { value: "promotion", label: "Promotion" },
    { value: "sample", label: "Sample" },
    { value: "transfer_error", label: "Transfer Error" },
    { value: "other", label: "Other" },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "adjustment_number",
        header: "Adjustment #",
        cell: ({ row }) => (
          <span className="font-medium text-primary">
            {row.original.adjustment_number}
          </span>
        ),
      },
      {
        accessorKey: "store",
        header: "Store",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.store?.name || "-"}</span>
        ),
      },
      {
        accessorKey: "reason_label",
        header: "Reason",
        cell: ({ row }) => {
          const record = row.original;

          if (!hasPermission("stock adjustment read")) {
            return <RecordNotFound />;
          }
          return (
            <div>
              <div className="font-medium">{record.reason_label}</div>
              {record.notes && (
                <div className="text-xs text-muted-foreground mt-1">
                  {record.notes.length > 50
                    ? `${record.notes.substring(0, 50)}...`
                    : record.notes}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "total_items",
        header: "Items",
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {row.original.total_items}
          </Badge>
        ),
      },
      {
        accessorKey: "total_adjustment_value",
        header: "Adjustment Value",
        cell: ({ row }) => {
          const value = parseFloat(row.original.total_adjustment_value);
          const isPositive = row.original.is_positive_adjustment;
          return (
            <div className="flex items-center gap-1">
              {isPositive ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600" />
              )}
              <span
                className={`font-semibold ${isPositive ? "text-green-600" : "text-red-600"}`}
              >
                {isPositive ? "+" : "-"}
                {currencySymbol}
                {Math.abs(value).toFixed(2)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            variant={getStatusBadgeVariant(row.original.status)}
            className="capitalize"
          >
            {row.original.status.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleView(row.original.id)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              {(row.original.status === "draft" ||
                row.original.status === "rejected") &&
                hasPermission("stock adjustment update") && (
                  <DropdownMenuItem onClick={() => handleEdit(row.original.id)}>
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
              {row.original.status === "draft" &&
                (hasPermission("stock adjustment create") ||
                  hasPermission("stock adjustment update")) && (
                  <DropdownMenuItem
                    onClick={() => handleSubmitAdjustment(row.original.id)}
                  >
                    <Send className="mr-2 h-4 w-4" /> Submit
                  </DropdownMenuItem>
                )}
              {row.original.status === "approved" &&
                hasPermission("stock adjustment process") && (
                  <DropdownMenuItem
                    onClick={() => handleProcess(row.original.id)}
                  >
                    <Play className="mr-2 h-4 w-4" /> Process
                  </DropdownMenuItem>
                )}
              {(row.original.status === "pending" ||
                row.original.status === "approved") &&
                (hasPermission("stock adjustment approve") ||
                  hasPermission("stock adjustment process")) && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => handleCancel(row.original.id)}
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Cancel
                  </DropdownMenuItem>
                )}
              {row.original.status === "draft" &&
                hasPermission("stock adjustment delete") && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => handleDeleteAdjustment(row.original.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol],
  );

  const hasApprovePermission = hasPermission("stock adjustment approve");

  const mainContent = (
    <>
      <FilterCard>
        <FilterSearch
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search adjustments..."
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          pageCount={paginationMeta.last_page}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          isLoading={isLoading}
          onRowClick={(row) => handleView(row.id)}
          enableRowSelection={false}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Adjustments: {paginationMeta.total || 0}
            </span>
          }
        />
      </TableCard>
    </>
  );

  return (
    <div className="page-wrapper">
      <div className="content p-6 max-w-[100vw] overflow-x-hidden">
        <PageHeader>
          <PageHeaderHeading
            title="Stock Adjustments"
            subtitle="Manage Inventory Adjustments"
          />
          <PageHeaderActions>
            {hasPermission("stock adjustment create") && (
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditingId(null);
                  setNewAdjustmentData({
                    store_id: currentStore?.id || "",
                    reason: "physical_count",
                    notes: "",
                    items: [],
                    status: "draft",
                  });
                  setShowAddModal(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Adjustment
              </Button>
            )}
            {hasPermission("stock adjustment reports") && (
              <Button variant="outline" onClick={handleOpenExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="mr-2 h-4 w-4" />
              {showFilters ? "Hide" : "Show"} Filters
            </Button>
          </PageHeaderActions>
        </PageHeader>

        {hasApprovePermission ? (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Adjustments</TabsTrigger>
              <TabsTrigger value="pending">Pending Approvals</TabsTrigger>
            </TabsList>
            <TabsContent value="all">{mainContent}</TabsContent>
            <TabsContent value="pending">
              <PendingApprovals isTab={true} />
            </TabsContent>
          </Tabs>
        ) : (
          mainContent
        )}
      </div>

      {/* Filter Modal */}
      <Modal
        open={showFilters}
        onOpenChange={setShowFilters}
        title="Filter Stock Adjustments"
        icon={Filter}
        maxWidth="max-w-3xl"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={handleResetFilters}>
              <X className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button variant="outline" onClick={handleApplyFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Apply Filters
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Store</label>
            <ReactSelect
              options={[
                { value: "", label: "All Stores" },
                ...stores.map((s) => ({
                  value: s.id.toString(),
                  label: s.name,
                })),
              ]}
              value={
                [
                  { value: "", label: "All Stores" },
                  ...stores.map((s) => ({
                    value: s.id.toString(),
                    label: s.name,
                  })),
                ].find((o) => o.value === filters.store_id) || {
                  value: "",
                  label: "All Stores",
                }
              }
              onChange={(val) =>
                setFilters({ ...filters, store_id: val?.value || "" })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <ReactSelect
              isMulti
              options={[
                { value: "draft", label: "Draft" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "processed", label: "Processed" },
                { value: "cancelled", label: "Cancelled" },
              ]}
              value={[
                { value: "draft", label: "Draft" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "processed", label: "Processed" },
                { value: "cancelled", label: "Cancelled" },
              ].filter((o) => filters.status.includes(o.value))}
              onChange={(val) =>
                setFilters({
                  ...filters,
                  status: val ? val.map((o) => o.value) : [],
                })
              }
              placeholder="Select statuses..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <ReactSelect
              isMulti
              options={reasonOptions}
              value={reasonOptions.filter((o) =>
                filters.reason.includes(o.value),
              )}
              onChange={(val) =>
                setFilters({
                  ...filters,
                  reason: val ? val.map((o) => o.value) : [],
                })
              }
              placeholder="Select reasons..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Sort By</label>
            <ReactSelect
              options={[
                { value: "created_at", label: "Created Date" },
                { value: "updated_at", label: "Updated Date" },
                { value: "adjustment_number", label: "Adjustment Number" },
                { value: "status", label: "Status" },
                { value: "reason", label: "Reason" },
                { value: "total_items", label: "Total Items" },
                { value: "total_adjustment_value", label: "Total Value" },
              ]}
              value={[
                { value: "created_at", label: "Created Date" },
                { value: "updated_at", label: "Updated Date" },
                { value: "adjustment_number", label: "Adjustment Number" },
                { value: "status", label: "Status" },
                { value: "reason", label: "Reason" },
                { value: "total_items", label: "Total Items" },
                { value: "total_adjustment_value", label: "Total Value" },
              ].find((o) => o.value === filters.sort_by)}
              onChange={(val) =>
                setFilters({
                  ...filters,
                  sort_by: val?.value || "created_at",
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Created From</label>
            <Input
              type="date"
              value={filters.created_from}
              onChange={(e) =>
                setFilters({ ...filters, created_from: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Created To</label>
            <Input
              type="date"
              value={filters.created_to}
              onChange={(e) =>
                setFilters({ ...filters, created_to: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Min Adjustment Value</label>
            <Input
              type="number"
              placeholder="0.00"
              value={filters.min_adjustment_value}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  min_adjustment_value: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Max Adjustment Value</label>
            <Input
              type="number"
              placeholder="0.00"
              value={filters.max_adjustment_value}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  max_adjustment_value: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Min Items</label>
            <Input
              type="number"
              placeholder="1"
              value={filters.min_items}
              onChange={(e) =>
                setFilters({ ...filters, min_items: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Max Items</label>
            <Input
              type="number"
              placeholder="100"
              value={filters.max_items}
              onChange={(e) =>
                setFilters({ ...filters, max_items: e.target.value })
              }
            />
          </div>
        </div>
      </Modal>

      {/* Export Modal */}
      <Modal
        open={showExportModal}
        onOpenChange={setShowExportModal}
        title="Export Stock Adjustments"
        icon={Download}
        maxWidth="max-w-3xl"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Store</label>
            <ReactSelect
              options={[
                { value: "", label: "All Stores" },
                ...stores.map((s) => ({
                  value: s.id.toString(),
                  label: s.name,
                })),
              ]}
              value={
                [
                  { value: "", label: "All Stores" },
                  ...stores.map((s) => ({
                    value: s.id.toString(),
                    label: s.name,
                  })),
                ].find((o) => o.value === exportSettings.store_id) || {
                  value: "",
                  label: "All Stores",
                }
              }
              onChange={(val) =>
                setExportSettings({
                  ...exportSettings,
                  store_id: val?.value || "",
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <ReactSelect
              isMulti
              options={[
                { value: "draft", label: "Draft" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "processed", label: "Processed" },
                { value: "cancelled", label: "Cancelled" },
              ]}
              value={[
                { value: "draft", label: "Draft" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "processed", label: "Processed" },
                { value: "cancelled", label: "Cancelled" },
              ].filter((o) => exportSettings.status.includes(o.value))}
              onChange={(val) =>
                setExportSettings({
                  ...exportSettings,
                  status: val ? val.map((o) => o.value) : [],
                })
              }
              placeholder="Select statuses..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <ReactSelect
              isMulti
              options={reasonOptions}
              value={reasonOptions.filter((o) =>
                exportSettings.reason.includes(o.value),
              )}
              onChange={(val) =>
                setExportSettings({
                  ...exportSettings,
                  reason: val ? val.map((o) => o.value) : [],
                })
              }
              placeholder="Select reasons..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Created From</label>
            <Input
              type="date"
              value={exportSettings.created_from}
              onChange={(e) =>
                setExportSettings({
                  ...exportSettings,
                  created_from: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Created To</label>
            <Input
              type="date"
              value={exportSettings.created_to}
              onChange={(e) =>
                setExportSettings({
                  ...exportSettings,
                  created_to: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Min Adjustment Value</label>
            <Input
              type="number"
              placeholder="0.00"
              value={exportSettings.min_adjustment_value}
              onChange={(e) =>
                setExportSettings({
                  ...exportSettings,
                  min_adjustment_value: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Max Adjustment Value</label>
            <Input
              type="number"
              placeholder="0.00"
              value={exportSettings.max_adjustment_value}
              onChange={(e) =>
                setExportSettings({
                  ...exportSettings,
                  max_adjustment_value: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Min Items</label>
            <Input
              type="number"
              placeholder="1"
              value={exportSettings.min_items}
              onChange={(e) =>
                setExportSettings({
                  ...exportSettings,
                  min_items: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Max Items</label>
            <Input
              type="number"
              placeholder="100"
              value={exportSettings.max_items}
              onChange={(e) =>
                setExportSettings({
                  ...exportSettings,
                  max_items: e.target.value,
                })
              }
            />
          </div>
        </div>
      </Modal>

      {/* View Details Modal */}
      <Modal
        open={showViewModal}
        onOpenChange={setShowViewModal}
        title={`Stock Adjustment Details - ${selectedAdjustment?.adjustment_number || ""}`}
        icon={Package}
        maxWidth="max-w-5xl"
      >
        {isViewLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : selectedAdjustment && (
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Adjustment Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Adjustment #:</span>
                    <span className="font-medium">
                      {selectedAdjustment.adjustment_number}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <Badge
                      variant={getStatusBadgeVariant(selectedAdjustment.status)}
                      className="capitalize"
                    >
                      {selectedAdjustment.status.replace(/_/g, " ")}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Store:</span>
                    <span className="font-medium">
                      {selectedAdjustment.store?.name || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reason:</span>
                    <span className="font-medium">
                      {selectedAdjustment.reason_label}
                    </span>
                  </div>
                  {[
                    "opening_balance",
                    "physical_count",
                    "found",
                    "customer_return",
                  ].includes(selectedAdjustment.reason) && (
                      <div className="text-[10px] text-blue-600 font-medium flex items-center gap-1 mt-1 justify-end">
                        <AlertCircle className="h-3 w-3" />
                        Auto-processes after approval
                      </div>
                    )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created At:</span>
                    <span className="font-medium">
                      {formatDate(selectedAdjustment.created_at)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Approval Setting:
                    </span>
                    <Badge variant="outline">
                      {selectedAdjustment.requires_approval
                        ? "Approval Required"
                        : "Self-Approving"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Items:</span>
                    <span className="font-medium">
                      {selectedAdjustment.total_items}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Adjustment Value:
                    </span>
                    <span
                      className={`font-bold ${selectedAdjustment.is_positive_adjustment ? "text-green-600" : "text-red-600"}`}
                    >
                      {selectedAdjustment.is_positive_adjustment ? "+" : "-"}
                      {currencySymbol}
                      {Math.abs(
                        parseFloat(selectedAdjustment.total_adjustment_value),
                      ).toFixed(2)}
                    </span>
                  </div>
                  {selectedAdjustment.notes && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground block mb-1">
                        Notes:
                      </span>
                      <p className="text-sm">{selectedAdjustment.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {selectedAdjustment.items &&
              selectedAdjustment.items.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Adjustment Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                      <ShadcnTable>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">
                              Current Qty
                            </TableHead>
                            <TableHead className="text-right">
                              Adjustment
                            </TableHead>
                            <TableHead className="text-right">
                              New Qty
                            </TableHead>
                            <TableHead className="text-right">
                              Unit Cost
                            </TableHead>
                            <TableHead className="text-right">
                              Total Value
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedAdjustment.items.map((item, index) => {
                            const adjustment = parseFloat(
                              item.adjustment_quantity || 0,
                            );
                            const current = parseFloat(
                              item.current_quantity || 0,
                            );
                            const newQty = current + adjustment;
                            const unitCost = parseFloat(item.unit_cost || 0);
                            const totalValue = Math.abs(adjustment * unitCost);

                            return (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">
                                  {index + 1}
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <div className="font-medium">
                                      {item.product?.name || "Unknown Product"}
                                    </div>
                                    {item.product?.sku && (
                                      <div className="text-xs text-muted-foreground">
                                        SKU: {item.product.sku}
                                      </div>
                                    )}
                                    {item.inventory?.location && (
                                      <div className="text-xs text-muted-foreground">
                                        Location: {item.inventory.location}
                                      </div>
                                    )}
                                    {item.inventory_batch?.batch_number && (
                                      <div className="text-xs text-muted-foreground">
                                        Batch: {item.inventory_batch.batch_number}
                                      </div>
                                    )}
                                    {item.reason_notes && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        {item.reason_notes}
                                      </div>
                                    )}
                                    {item.serial_numbers && item.serial_numbers.length > 0 && (
                                      <div className="text-xs text-muted-foreground mt-2 border-t pt-1">
                                        <span className="font-medium mr-1">Serials:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {item.serial_numbers.map((sn, i) => (
                                            <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">
                                              {sn}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {current}
                                </TableCell>
                                <TableCell
                                  className={`text-right font-semibold ${adjustment >= 0 ? "text-green-600" : "text-red-600"}`}
                                >
                                  {adjustment >= 0 ? "+" : ""}
                                  {adjustment}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {newQty}
                                </TableCell>
                                <TableCell className="text-right">
                                  {currencySymbol}
                                  {unitCost.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {currencySymbol}
                                  {totalValue.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </ShadcnTable>
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Action Footer for View Modal */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              {selectedAdjustment.status === "draft" &&
                (hasPermission("stock adjustment create") ||
                  hasPermission("stock adjustment update")) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewModal(false);
                      handleSubmitAdjustment(selectedAdjustment.id);
                    }}
                    className="gap-2"
                  >
                    <Send className="h-4 w-4" />
                    Submit for Approval
                  </Button>
                )}

              {selectedAdjustment.status === "approved" &&
                hasPermission("stock adjustment process") && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewModal(false);
                      handleProcess(selectedAdjustment.id);
                    }}
                    className="gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Process Adjustment
                  </Button>
                )}

              {(selectedAdjustment.status === "pending" ||
                selectedAdjustment.status === "approved") &&
                (hasPermission("stock adjustment approve") ||
                  hasPermission("stock adjustment process")) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewModal(false);
                      handleCancel(selectedAdjustment.id);
                    }}
                    className="gap-2 text-destructive border-destructive/20 hover:bg-destructive/10"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel Adjustment
                  </Button>
                )}

              {(selectedAdjustment.status === "draft" ||
                selectedAdjustment.status === "rejected") &&
                hasPermission("stock adjustment update") && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewModal(false);
                      handleEdit(selectedAdjustment.id);
                    }}
                    className="gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    {selectedAdjustment.status === "rejected"
                      ? "Edit & Resubmit"
                      : "Edit Draft"}
                  </Button>
                )}
            </div>
          </div>
        )}
      </Modal>

      {/* Add Stock Adjustment Modal */}
      <Modal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        title={`${isEditing ? "Edit" : "New"} Stock Adjustment`}
        icon={PlusCircle}
        maxWidth="max-w-[90vw]"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSaveNewAdjustment(false)}
              disabled={
                saveNewMutation.isPending ||
                newAdjustmentData.items.length === 0
              }
              className="gap-2"
            >
              {saveNewMutation.isPending &&
                !saveNewMutation.variables?.shouldSubmit ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PlusCircle className="h-4 w-4" />
              )}
              {isEditing ? "Update as Draft" : "Save as Draft"}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSaveNewAdjustment(true)}
              disabled={
                saveNewMutation.isPending ||
                newAdjustmentData.items.length === 0
              }
              className="gap-2"
            >
              {saveNewMutation.isPending &&
                saveNewMutation.variables?.shouldSubmit ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isEditing ? "Update & Submit" : "Save & Submit"}
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Store *</label>
              <ReactSelect
                options={stores.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                value={stores
                  .map((s) => ({ value: s.id, label: s.name }))
                  .find((o) => o.value === newAdjustmentData.store_id)}
                onChange={(val) =>
                  setNewAdjustmentData({
                    ...newAdjustmentData,
                    store_id: val?.value || "",
                    items: [],
                  })
                }
                placeholder="Select Store"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Reason *</label>
              <ReactSelect
                options={
                  newAdjustmentData.items.some((it) => it.inventory_id)
                    ? reasonOptions.filter((o) => o.value !== "opening_balance")
                    : reasonOptions
                }
                value={reasonOptions.find(
                  (o) => o.value === newAdjustmentData.reason,
                )}
                onChange={(val) => {
                  if (
                    val?.value === "opening_balance" &&
                    newAdjustmentData.items.some((it) => it.inventory_id)
                  ) {
                    toast.error(
                      "Opening stock can only be added if there are no existing inventory records.",
                    );
                    return;
                  }
                  setNewAdjustmentData({
                    ...newAdjustmentData,
                    reason: val?.value || "physical_count",
                  });
                }}
                placeholder="Select Reason"
              />
              {newAdjustmentData.items.some((it) => it.inventory_id) &&
                newAdjustmentData.reason === "opening_balance" && (
                  <p className="text-[11px] text-destructive font-medium flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" />
                    Opening stock cannot be added for items with existing inventory records.
                  </p>
                )}
              {[
                "opening_balance",
                "physical_count",
                "found",
                "customer_return",
              ].includes(newAdjustmentData.reason) && (
                  <p className="text-[11px] text-blue-600 font-medium flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" />
                    This adjustment type will be automatically processed once
                    approved.
                  </p>
                )}
            </div>

            <div className="md:col-span-2 space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Internal notes about this adjustment..."
                value={newAdjustmentData.notes}
                onChange={(e) =>
                  setNewAdjustmentData({
                    ...newAdjustmentData,
                    notes: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex items-center space-x-2 py-2">
              <Switch
                id="requires-approval"
                checked={newAdjustmentData.requires_approval}
                onCheckedChange={(checked) =>
                  setNewAdjustmentData((prev) => ({
                    ...prev,
                    requires_approval: checked,
                  }))
                }
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="requires-approval"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Requires Approval
                </Label>
                <p className="text-xs text-muted-foreground">
                  {newAdjustmentData.requires_approval
                    ? "This adjustment will need to be approved before processing."
                    : "This adjustment will be automatically approved upon submission."}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4 py-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  Adjustment Items
                </h3>
              </div>
              <div className="space-y-2">
                <Label>Search & Add Product</Label>
                <ReactSelect
                  placeholder="Search products by name, SKU or barcode..."
                  onInputChange={(val) => setProductSearch(val)}
                  options={posProducts.map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.sku || "-"}) - Stock: ${p.sellable_qty || 0}`,
                    product: p,
                  }))}
                  onChange={(opt) => handleAddProduct(opt?.product)}
                  isLoading={isSearchingProducts}
                  isDisabled={!newAdjustmentData.store_id}
                />
                {!newAdjustmentData.store_id && (
                  <p className="text-[10px] text-destructive">
                    Select a store first to search products
                  </p>
                )}
              </div>
            </div>

            {newAdjustmentData.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 border rounded-lg border-dashed bg-muted/20">
                <Package className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No items added yet. Search and add products above.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <ShadcnTable>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Tracking info</TableHead>
                      <TableHead className="text-right">Current Qty</TableHead>
                      <TableHead className="text-right">
                        Adjustment (+/-)
                      </TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead>Item Notes</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newAdjustmentData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="min-w-[200px]">
                          <div>
                            <div className="font-medium">
                              {item.product_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {item.sku}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="min-w-[150px]">
                          {item.tracking_type === "standard" ? (
                            <div className="space-y-1">
                              <ReactSelect
                                options={item.available_batches.map((batch) => ({
                                  value: batch.id.toString(),
                                  label: `${batch.batch_number} (Qty: ${batch.quantity})`,
                                }))}
                                value={
                                  item.available_batches.find(
                                    (batch) =>
                                      batch.id.toString() ===
                                      item.inventory_batch_id?.toString(),
                                  )
                                    ? {
                                      value: item.inventory_batch_id.toString(),
                                      label: `${item.available_batches.find(
                                        (batch) =>
                                          batch.id.toString() ===
                                          item.inventory_batch_id?.toString(),
                                      ).batch_number
                                        } (Qty: ${item.available_batches.find(
                                          (batch) =>
                                            batch.id.toString() ===
                                            item.inventory_batch_id?.toString(),
                                        ).quantity
                                        })`,
                                    }
                                    : null
                                }
                                onChange={(option) =>
                                  handleItemChange(
                                    index,
                                    "inventory_batch_id",
                                    option?.value,
                                  )
                                }
                                placeholder={
                                  item.available_batches?.length > 0
                                    ? (newAdjustmentData.reason === "opening_balance"
                                      ? "Select Batch (Optional)"
                                      : "Select Batch *")
                                    : "No existing batches"
                                }
                                isClearable={newAdjustmentData.reason === "opening_balance"}
                                isDisabled={item.available_batches?.length === 0}
                                className="w-full"
                              />
                              {!item.inventory_batch_id &&
                                newAdjustmentData.reason !== "opening_balance" &&
                                item.available_batches?.length > 0 && (
                                  <p className="text-[10px] text-destructive font-medium flex items-center gap-1 mt-1">
                                    <AlertCircle className="h-3 w-3" />
                                    Batch is required for stock adjustments
                                  </p>
                                )}
                              {item.available_batches?.length === 0 &&
                                newAdjustmentData.reason !== "opening_balance" && (
                                  <p className="text-[10px] text-amber-600 font-medium flex items-center gap-1 mt-1">
                                    <AlertCircle className="h-3 w-3" />
                                    {item.inventory_id
                                      ? "No batches found for this product inventory."
                                      : "No batches found. Change reason to \"Opening Balance\" to initialize stock."}
                                  </p>
                                )}
                            </div>
                          ) : item.tracking_type === "serial" ? (
                            <div className="space-y-1">
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-primary/5 text-blue-600 border-primary/20"
                              >
                                <ScanBarcode className="h-3 w-3 mr-1" />
                                Serial Tracked
                              </Badge>

                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full h-8 justify-between text-xs font-normal px-2"
                                onClick={() => {
                                  setActiveSerialIndex(index);

                                  // Find the last serial number to suggest the next one
                                  const availableSns =
                                    item.available_serials || [];
                                  const lastSnObj = [...availableSns]
                                    .sort((a, b) =>
                                      a.serial_number.localeCompare(
                                        b.serial_number,
                                        undefined,
                                        { numeric: true },
                                      ),
                                    )
                                    .pop();
                                  let nextNum = "";
                                  let detectedPrefix = item.sku
                                    ? `${item.sku}-`
                                    : "";

                                  if (lastSnObj) {
                                    const match =
                                      lastSnObj.serial_number.match(
                                        /^(.*?)(\d+)$/,
                                      );
                                    if (match) {
                                      detectedPrefix = match[1];
                                      nextNum = (
                                        parseInt(match[2]) + 1
                                      ).toString();
                                    }
                                  }

                                  setSerialGenParams({
                                    startNumber: nextNum,
                                    quantity: 1,
                                    prefix: detectedPrefix,
                                  });

                                  setShowSerialModal(true);
                                }}
                              >
                                {item.serial_numbers.length > 0 ? (
                                  <>
                                    <span className="truncate">
                                      {item.serial_numbers.length} Serials
                                    </span>
                                    <Edit className="h-3 w-3 opacity-50" />
                                  </>
                                ) : (
                                  <>
                                    <span>Setup Serials</span>
                                    <Plus className="h-3 w-3 opacity-50" />
                                  </>
                                )}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground px-2">
                              Not tracked
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {item.current_quantity}
                        </TableCell>
                        <TableCell className="w-[120px]">
                          <Input
                            type="number"
                            value={item.adjustment_quantity}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "adjustment_quantity",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            className={cn(
                              "text-right font-bold",
                              item.tracking_type === "serial" && "bg-muted",
                            )}
                            readOnly={item.tracking_type === "serial"}
                          />
                        </TableCell>
                        <TableCell className="w-[120px]">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.unit_cost}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "unit_cost",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="Reason for this item..."
                            value={item.reason_notes}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "reason_notes",
                                e.target.value,
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            className="text-destructive"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </ShadcnTable>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Serial Selection Modal */}
      <Modal
        open={showSerialModal}
        onOpenChange={setShowSerialModal}
        title={`Manage Serials - ${newAdjustmentData.items[activeSerialIndex]?.product_name || ""}`}
        icon={ScanBarcode}
        maxWidth="max-w-md"
        footer={
          <div className="flex justify-end w-full">
            <Button onClick={() => setShowSerialModal(false)}>Done</Button>
          </div>
        }
      >
        {activeSerialIndex !== null &&
          newAdjustmentData.items[activeSerialIndex] && (
            <div className="space-y-4 py-4">
              <div className="space-y-3 pb-4 border-b">
                <Label className="text-xs font-bold text-muted-foreground uppercase">
                  Select Adjustment Type
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    type="button"
                    variant={
                      newAdjustmentData.items[activeSerialIndex]
                        .adjustment_quantity > 0
                        ? "default"
                        : "outline"
                    }
                    className={cn(
                      "h-16 flex flex-col gap-1",
                      newAdjustmentData.items[activeSerialIndex]
                        .adjustment_quantity > 0
                        ? "border-primary bg-primary text-primary-foreground"
                        : "hover:bg-muted",
                    )}
                    onClick={() =>
                      handleItemChange(
                        activeSerialIndex,
                        "adjustment_quantity",
                        Math.max(
                          1,
                          Math.abs(
                            newAdjustmentData.items[activeSerialIndex]
                              .adjustment_quantity || 0,
                          ),
                        ),
                      )
                    }
                  >
                    <PlusCircle size={20} />
                    <span>Add Stock</span>
                  </Button>
                  <Button
                    type="button"
                    variant={
                      newAdjustmentData.items[activeSerialIndex]
                        .adjustment_quantity < 0
                        ? "destructive"
                        : "outline"
                    }
                    className={cn(
                      "h-16 flex flex-col gap-1",
                      newAdjustmentData.items[activeSerialIndex]
                        .adjustment_quantity < 0
                        ? "border-destructive bg-destructive text-destructive-foreground"
                        : "hover:bg-muted",
                    )}
                    onClick={() =>
                      handleItemChange(
                        activeSerialIndex,
                        "adjustment_quantity",
                        -Math.max(
                          1,
                          Math.abs(
                            newAdjustmentData.items[activeSerialIndex]
                              .adjustment_quantity || 0,
                          ),
                        ),
                      )
                    }
                  >
                    <TrendingDown size={20} />
                    <span>Remove Stock</span>
                  </Button>
                </div>
              </div>

              {newAdjustmentData.items[activeSerialIndex]
                .adjustment_quantity !== 0 && (
                  <>
                    {newAdjustmentData.items[activeSerialIndex]
                      .adjustment_quantity < 0 ? (
                      <div className="space-y-2">
                        <Label>Select Existing Serials</Label>
                        <ReactSelect
                          isMulti
                          options={(
                            newAdjustmentData.items[activeSerialIndex]
                              .available_serials || []
                          ).map((s) => ({
                            value: s.serial_number,
                            label: s.serial_number,
                            status: s.status_info?.label,
                            variant: s.status_info?.variant,
                          }))}
                          formatOptionLabel={(option) => (
                            <div className="flex justify-between items-center w-full min-w-[200px]">
                              <span className="font-mono text-sm">
                                {option.label}
                              </span>
                              {option.status && (
                                <Badge
                                  variant={option.variant || "secondary"}
                                  className="text-[10px] h-4"
                                >
                                  {option.status}
                                </Badge>
                              )}
                            </div>
                          )}
                          value={(
                            newAdjustmentData.items[activeSerialIndex]
                              .serial_numbers || []
                          ).map((sn) => ({ value: sn, label: sn }))}
                          onChange={(selected) =>
                            handleItemChange(
                              activeSerialIndex,
                              "serial_numbers",
                              selected ? selected.map((o) => o.value) : [],
                            )
                          }
                          placeholder="Search and select serials..."
                        />
                        <p className="text-[10px] text-muted-foreground">
                          Only serials currently in stock for this store are
                          shown.
                          {newAdjustmentData.items[activeSerialIndex]
                            .available_serials?.length > 0 && (
                              <span className="block mt-1 font-semibold text-primary">
                                Last serial:{" "}
                                {
                                  [
                                    ...newAdjustmentData.items[activeSerialIndex]
                                      .available_serials,
                                  ]
                                    .sort((a, b) =>
                                      a.serial_number.localeCompare(
                                        b.serial_number,
                                        undefined,
                                        { numeric: true, sensitivity: "base" },
                                      ),
                                    )
                                    .pop()?.serial_number
                                }
                              </span>
                            )}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {hasPermission("stock adjustment bulk_operations") && (
                          <div className="bg-muted/30 p-3 rounded-md space-y-3">
                            <div className="flex justify-between items-center">
                              <Label className="text-xs font-bold uppercase text-muted-foreground">
                                Bulk Generate
                              </Label>
                              {newAdjustmentData.items[activeSerialIndex]
                                .available_serials?.length > 0 && (
                                  <span className="text-[10px] text-primary">
                                    Last:{" "}
                                    {
                                      [
                                        ...newAdjustmentData.items[
                                          activeSerialIndex
                                        ].available_serials,
                                      ]
                                        .sort((a, b) =>
                                          a.serial_number.localeCompare(
                                            b.serial_number,
                                            undefined,
                                            { numeric: true },
                                          ),
                                        )
                                        .pop()?.serial_number
                                    }
                                  </span>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-[10px]">
                                  Start Number
                                </Label>
                                <Input
                                  type="text"
                                  placeholder="e.g. 1001"
                                  className="h-8 text-xs font-mono"
                                  value={serialGenParams.startNumber}
                                  onChange={(e) =>
                                    setSerialGenParams({
                                      ...serialGenParams,
                                      startNumber: e.target.value.replace(
                                        /\D/g,
                                        "",
                                      ),
                                    })
                                  }
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[10px]">Quantity</Label>
                                <Input
                                  type="number"
                                  className="h-8 text-xs"
                                  value={serialGenParams.quantity}
                                  min={1}
                                  onChange={(e) =>
                                    setSerialGenParams({
                                      ...serialGenParams,
                                      quantity: parseInt(e.target.value) || 1,
                                    })
                                  }
                                />
                              </div>
                            </div>

                            <div className="space-y-1">
                              <Label className="text-[10px]">
                                Prefix (Optional)
                              </Label>
                              <Input
                                type="text"
                                placeholder="e.g. SN-"
                                className="h-8 text-xs font-mono"
                                value={serialGenParams.prefix}
                                onChange={(e) =>
                                  setSerialGenParams({
                                    ...serialGenParams,
                                    prefix: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <Button
                              className="w-full h-8 text-xs gap-1"
                              variant="secondary"
                              onClick={() => {
                                const { startNumber, quantity, prefix } =
                                  serialGenParams;
                                if (!startNumber) {
                                  toast.error("Please enter a starting number");
                                  return;
                                }

                                const startNum = parseInt(startNumber);
                                const availableSns =
                                  newAdjustmentData.items[activeSerialIndex]
                                    .available_serials || [];
                                const currentSns =
                                  newAdjustmentData.items[activeSerialIndex]
                                    .serial_numbers || [];

                                // Check against "greater than last" if last exists
                                const lastSnObj = [...availableSns]
                                  .sort((a, b) =>
                                    a.serial_number.localeCompare(
                                      b.serial_number,
                                      undefined,
                                      { numeric: true },
                                    ),
                                  )
                                  .pop();
                                if (lastSnObj) {
                                  const lastMatch =
                                    lastSnObj.serial_number.match(/\d+$/);
                                  if (lastMatch) {
                                    const lastNum = parseInt(lastMatch[0]);
                                    if (startNum <= lastNum) {
                                      toast.warning(
                                        `Starting number (${startNum}) is not greater than the last serial number (${lastNum})`,
                                      );
                                    }
                                  }
                                }

                                const newSerials = [];
                                let existingCount = 0;

                                for (let i = 0; i < quantity; i++) {
                                  const num = (startNum + i)
                                    .toString()
                                    .padStart(startNumber.length, "0");
                                  const sn = `${prefix}${num}`;

                                  // Check if it already exists in available or current
                                  if (
                                    availableSns.some(
                                      (s) => s.serial_number === sn,
                                    ) ||
                                    currentSns.includes(sn)
                                  ) {
                                    existingCount++;
                                    continue;
                                  }
                                  newSerials.push(sn);
                                }

                                if (newSerials.length > 0) {
                                  handleItemChange(
                                    activeSerialIndex,
                                    "serial_numbers",
                                    [...currentSns, ...newSerials],
                                  );
                                  toast.success(
                                    `Generated ${newSerials.length} serials`,
                                  );
                                }

                                if (existingCount > 0) {
                                  toast.warning(
                                    `${existingCount} serials skipped because they already exist`,
                                  );
                                }
                              }}
                            >
                              <Wand2 className="h-3 w-3" /> Generate Serials
                            </Button>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label className="flex justify-between">
                            Serials List (Click to edit manually)
                          </Label>
                          <Textarea
                            placeholder="Enter serial numbers separated by character (one per line)..."
                            value={(
                              newAdjustmentData.items[activeSerialIndex]
                                .serial_numbers || []
                            ).join("\n")}
                            onChange={(e) => {
                              const lines = e.target.value.split("\n");
                              handleItemChange(
                                activeSerialIndex,
                                "serial_numbers",
                                lines,
                              );
                            }}
                            className="min-h-[120px] font-mono text-sm py-2"
                          />
                        </div>
                        <div className="flex justify-between items-center text-xs text-muted-foreground bg-muted p-2 rounded">
                          <span>
                            Total Serials:{" "}
                            {
                              (
                                newAdjustmentData.items[activeSerialIndex]
                                  .serial_numbers || []
                              ).filter((s) => s.trim() !== "").length
                            }
                          </span>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() =>
                              handleItemChange(
                                activeSerialIndex,
                                "serial_numbers",
                                [],
                              )
                            }
                            className="h-6 text-destructive"
                          >
                            Clear All
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
            </div>
          )}
      </Modal>

      {/* Confirmation Dialog */}
      <Modal
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, isOpen: open })
        }
        title={confirmDialog.title}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({ ...confirmDialog, isOpen: false })
              }
            >
              Cancel
            </Button>
            <Button
              variant={
                confirmDialog.variant === "destructive"
                  ? "destructive"
                  : "outline"
              }
              onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog({ ...confirmDialog, isOpen: false });
              }}
            >
              Continue
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <div className="text-sm text-muted-foreground whitespace-pre-wrap">
            {confirmDialog.description}
          </div>
        </div>
      </Modal>

      {/* Action Input Dialog */}
      <Modal
        open={inputDialog.isOpen}
        onOpenChange={(open) =>
          setInputDialog({ ...inputDialog, isOpen: open })
        }
        title={inputDialog.title}
        maxWidth="max-w-lg"
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button
              variant="outline"
              onClick={() => setInputDialog({ ...inputDialog, isOpen: false })}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              disabled={inputDialog.isRequired && !inputDialog.value.trim()}
              onClick={() => {
                inputDialog.onConfirm(inputDialog.value);
                setInputDialog({ ...inputDialog, isOpen: false });
              }}
            >
              Proceed
            </Button>
          </div>
        }
      >
        <div className="py-4 space-y-4">
          {inputDialog.description && (
            <p className="text-sm text-muted-foreground font-medium">
              {inputDialog.description}
            </p>
          )}
          <div className="space-y-2">
            <Label>{inputDialog.label}</Label>
            <Textarea
              placeholder={inputDialog.placeholder}
              value={inputDialog.value}
              onChange={(e) =>
                setInputDialog({ ...inputDialog, value: e.target.value })
              }
              className="min-h-[100px]"
              autoFocus
            />
            {inputDialog.isRequired && !inputDialog.value.trim() && (
              <p className="text-[10px] text-destructive">
                This field is required.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StockAdjustments;
